import { Component, EventEmitter, inject, InjectionToken, Input, OnChanges, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MadCloudResult } from '@common/http/models/mad-cloud-result.model';
import { propertyValue } from '@common/util/properties';
import { PropertyObservable } from '@common/util/property-observable.decorator';
import { environment } from '@env/environment';
import { AuthUrlService } from '@portal-core/auth/services/auth-url.service';
import { MaxAvatarImageFileSizeBytes } from '@portal-core/data/common/constants/max-avatar-image-file-size.constant';
import { SizeBytes } from '@portal-core/data/enums/size-bytes.enum';
import { ErrorService } from '@portal-core/errors/services/error.service';
import { ImagePickerValue } from '@portal-core/forms/models/image-picker-value.model';
import { FormsService } from '@portal-core/forms/services/forms.service';
import { LicenseStorage } from '@portal-core/license-storage/models/license-storage.model';
import { LicenseStorageService } from '@portal-core/license-storage/services/license-storage.service';
import { LicenseSettings } from '@portal-core/licenses/models/license-settings.model';
import { License } from '@portal-core/licenses/models/license.model';
import { LicensesService } from '@portal-core/licenses/services/licenses.service';
import { SitesService } from '@portal-core/sites/services/sites.service';
import { AutoUnsubscribe } from '@portal-core/util/auto-unsubscribe.decorator';
import { LoadingState } from '@portal-core/util/loading-state';
import { WINDOW } from '@portal-core/util/window.provider';
import { Observable, Subscription, combineLatest, first, of, startWith, switchMap } from 'rxjs';
import { ImagePickerComponent } from '@portal-core/forms/components/image-picker/image-picker.component';
import { MatError, MatFormField, MatLabel, MatHint } from '@angular/material/form-field';
import { FormControlErrorMessageComponent } from '@portal-core/forms/components/form-control-error-message/form-control-error-message.component';
import { McColorDirective } from '@portal-core/ui/themes/directives/mc-color.directive';
import { MatInput } from '@angular/material/input';
import { NgIf, AsyncPipe } from '@angular/common';
import { MatSelect, MatOption } from '@angular/material/select';
import { ErrorsExpansionPanelComponent } from '@portal-core/general/components/errors-expansion-panel/errors-expansion-panel.component';
import { MatButton } from '@angular/material/button';
import { LoaderComponent } from '@portal-core/general/components/loader/loader.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { FileSizePipe } from '@portal-core/general/pipes/file-size/file-size.pipe';

export interface LicenseSettingsFormOptions {
  showAdminFields?: boolean | (() => boolean);
  redirectOnVanityChange?: boolean | (() => boolean);
}
export const MC_LICENSE_SETTINGS_FORM_OPTIONS = new InjectionToken<LicenseSettingsFormOptions>('LicenseSettingsFormOptions');

@Component({
    selector: 'mc-license-settings-form',
    templateUrl: './license-settings-form.component.html',
    styleUrls: ['./license-settings-form.component.scss'],
    encapsulation: ViewEncapsulation.None,
    imports: [ReactiveFormsModule, ImagePickerComponent, MatError, FormControlErrorMessageComponent, MatFormField, McColorDirective, MatLabel, MatInput, MatHint, NgIf, MatSelect, MatOption, ErrorsExpansionPanelComponent, MatButton, LoaderComponent, MatProgressSpinner, AsyncPipe, FileSizePipe]
})
@AutoUnsubscribe()
export class LicenseSettingsFormComponent implements OnInit, OnChanges {
  private readonly licenseSettingsFormOptions = inject<LicenseSettingsFormOptions>(MC_LICENSE_SETTINGS_FORM_OPTIONS, { optional: true });
  private readonly window = inject<Window>(WINDOW);
  private readonly formBuilder = inject(UntypedFormBuilder);
  private readonly licensesService = inject(LicensesService);
  private readonly errorService = inject(ErrorService);
  private readonly licenseStorageService = inject(LicenseStorageService);
  private readonly formsService = inject(FormsService);
  private readonly authUrlService = inject(AuthUrlService);
  private readonly sitesService = inject(SitesService);
  @Input() license: License;
  @Output() canceled: EventEmitter<void> = new EventEmitter<void>();
  @Output() saved: EventEmitter<void> = new EventEmitter<void>();
  @PropertyObservable('license') license$: Observable<License>;

  SizeBytes: typeof SizeBytes = SizeBytes;

  adminLicenseDataSubscription: Subscription;
  centralPortalDomain: string = new URL(environment.centralPortalBaseUri).host;
  changingVanity: boolean = false;
  licenseSettingsForm: UntypedFormGroup;
  loadingState: LoadingState<string> = new LoadingState<string>();
  outputDomain$: Observable<string>;
  redirectOnVanityChange: boolean = true;
  savingState: LoadingState<string> = new LoadingState<string>();
  showAdminFields: boolean = false;

  constructor() {
    this.buildForm();
  }

  ngOnInit() {
    this.showAdminFields = propertyValue(this.licenseSettingsFormOptions, 'showAdminFields');
    this.redirectOnVanityChange = propertyValue(this.licenseSettingsFormOptions, 'redirectOnVanityChange', true);

    this.outputDomain$ = this.licenseSettingsForm.get('vanityBaseUrl').valueChanges.pipe(
      startWith(this.licenseSettingsForm.get('vanityBaseUrl').value),
      switchMap(vanityBaseUrl => this.sitesService.getLiveSiteDomain$(vanityBaseUrl))
    );

    // Load the license storage data whenever the license changes and track whether the current user is an admin
    this.adminLicenseDataSubscription = this.license$.pipe(
      switchMap(license => license ? this.licenseStorageService.getItemById$(this.license.Id) : of(null))
    ).subscribe(licenseStorage => {
      this.rebuildForm(this.showAdminFields, this.license || {} as License, licenseStorage);
    });

    this.loadingState.update(true);

    // Show the loader while the required initial data is loaded
    combineLatest([
      this.license$,
      this.license$.pipe(
        switchMap(license => license ? this.licenseStorageService.getItemById$(this.license.Id) : of(null))
      )
    ]).pipe(
      first()
    ).subscribe(() => {
      this.loadingState.update(false);
    }, error => {
      this.loadingState.update(false, 'There was a problem loading the license settings.', this.errorService.getErrorMessages(error));
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.license) {
      this.resetForm(this.license || {} as License);
    }
  }

  onSubmit() {
    if (!this.licenseSettingsForm.valid) {
      return;
    }

    this.savingState.update(true);

    let request$: Observable<MadCloudResult<void>>;
    const newLicenseSettings: LicenseSettings = {
      KeyLabel: this.licenseSettingsForm.get('keyLabel').value,
      VanityBaseUrl: this.licenseSettingsForm.get('vanityBaseUrl').value,
      Description: this.licenseSettingsForm.get('description').value
    };
    const avatarValue: ImagePickerValue = this.licenseSettingsForm.value['avatar'];

    if (this.showAdminFields) {
      request$ = this.licensesService.changeAdminLicenseSettings$(this.license.Id, {
        ...newLicenseSettings,
        MaxStorageSize: this.licenseSettingsForm.get('maxStorage').value * this.licenseSettingsForm.get('bytes').value,
        AuthorSeats: this.licenseSettingsForm.get('maxAuthorSeats').value,
        SmeSeats: this.licenseSettingsForm.get('maxSmeSeats').value
      }, avatarValue ? avatarValue.file : null);
    } else {
      request$ = this.licensesService.changeLicenseSettings$(this.license.Id, newLicenseSettings, avatarValue ? avatarValue.file : null);
    }

    this.changingVanity = newLicenseSettings.VanityBaseUrl !== this.license?.VanityBaseUrl;

    // Submit the settings
    request$.subscribe(() => {
      // Only restore the form and emit saved when not changing the vanity. OR if the user is a sys admin since that means they are using portal-admin.
      // We don't want any extra HTTP requests going out if the vanity was changed because they could result in a 403 sending the user to the alert page.
      // Keep the loader up while redirecting to the new url.

      if (this.redirectOnVanityChange && this.changingVanity) {
        // If the vanity changed then the user needs to be sent to the new subdomain
        this.window.location.href = this.authUrlService.buildSubdomainUrl(newLicenseSettings.VanityBaseUrl);
      } else {
        this.savingState.update(false);
        this.licenseSettingsForm.markAsPristine();
        this.saved.emit();
      }
    }, error => {
      this.savingState.update(false, 'Unable to update the license settings.', this.errorService.getErrorMessages(error));
    });
  }

  onCancelClicked() {
    this.canceled.emit();
  }

  private buildForm() {
    this.licenseSettingsForm = this.formBuilder.group({
      avatar: new UntypedFormControl(null, this.formsService.createFileSizeValidator(MaxAvatarImageFileSizeBytes)),
      keyLabel: new UntypedFormControl(null, Validators.required),
      vanityBaseUrl: new UntypedFormControl(null, [Validators.required, Validators.pattern('^[A-Za-z0-9-]+$'), Validators.maxLength(128)]),
      description: new UntypedFormControl(null, [Validators.maxLength(250)])
    });
  }

  private resetForm(license: License) {
    this.licenseSettingsForm.reset({
      avatar: license.Avatar ? this.formsService.buildImagePickerValue(license.Avatar) : null,
      keyLabel: license.KeyLabel ?? '',
      vanityBaseUrl: license.VanityBaseUrl ?? '',
      description: license.Description ?? '',
      maxAuthorSeats: license.AuthorSeats ?? 0,
      maxSmeSeats: license.SmeSeats ?? 0,
      maxStorage: (license.MaxStorageSize / SizeBytes.Gigabyte),
      bytes: SizeBytes.Gigabyte
    });
  }

  private rebuildForm(showAdminFields: boolean, license: License, licenseStorage: LicenseStorage) {
    if (showAdminFields) {
      this.licenseSettingsForm.addControl('maxAuthorSeats', new UntypedFormControl(license.AuthorSeats ?? 0, [Validators.required, Validators.min(licenseStorage.UsedAuthorSeats), Validators.max(10000)]));
      this.licenseSettingsForm.addControl('maxSmeSeats', new UntypedFormControl(license.SmeSeats ?? 0, [Validators.required, Validators.min(licenseStorage.UsedSmeSeats), Validators.max(10000)]));
      this.licenseSettingsForm.addControl('maxStorage', new UntypedFormControl((license.MaxStorageSize / SizeBytes.Gigabyte), [Validators.required, this.formsService.createStorageSizeValidator('bytes', licenseStorage.UsedStorageSpace)]));
      this.licenseSettingsForm.addControl('bytes', new UntypedFormControl(SizeBytes.Gigabyte));
    } else {
      this.licenseSettingsForm.removeControl('maxAuthorSeats');
      this.licenseSettingsForm.removeControl('maxSmeSeats');
      this.licenseSettingsForm.removeControl('maxStorage');
      this.licenseSettingsForm.removeControl('bytes');
    }
  }
}
